import { useMemo, useState } from 'react'
import { Check, Upload } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog'
import { Button } from './ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs'
import { AVATAR_ICON_LIBRARY, Avatar } from './ui/avatar'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import { updateMyAvatar, uploadAvatarPhoto } from '../services/userService'
import { cn } from '../lib/utils'

const ICON_KEYS = Object.keys(AVATAR_ICON_LIBRARY)

export default function AvatarPicker({
  open,
  onOpenChange,
  currentAvatarType,
  currentAvatarUrl,
  userId,
  googleAvatarUrl,
  onSaved,
}) {
  const notify = useNotification()
  const { user: authUser } = useAuth()

  const initialTab = currentAvatarType === 'photo'
    ? 'photo'
    : currentAvatarType === 'google'
    ? 'google'
    : 'icon'
  const [tab, setTab] = useState(initialTab)
  const [selectedIcon, setSelectedIcon] = useState(currentAvatarType === 'icon' ? currentAvatarUrl : null)
  const [uploading, setUploading] = useState(false)
  const [saving, setSaving] = useState(false)

  const derivedGoogleUrl = useMemo(() => {
    if (googleAvatarUrl) return googleAvatarUrl
    if (!authUser?.identities) return null
    const google = authUser.identities.find((i) => i.provider === 'google')
    return google?.identity_data?.avatar_url || null
  }, [googleAvatarUrl, authUser])

  const persistAvatar = async ({ avatarType, avatarUrl }) => {
    setSaving(true)
    try {
      await updateMyAvatar({ avatarType, avatarUrl })
      notify.success('Avatar updated')
      onSaved?.()
      onOpenChange?.(false)
    } catch (err) {
      console.error('Failed to update avatar:', err)
      notify.error(err?.message || 'Failed to update avatar')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveIcon = () => {
    if (!selectedIcon) {
      notify.error('Pick an icon first')
      return
    }
    persistAvatar({ avatarType: 'icon', avatarUrl: selectedIcon })
  }

  const handlePickFile = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    if (!userId) {
      notify.error('Not signed in')
      return
    }
    if (!file.type.startsWith('image/')) {
      notify.error('Please choose an image file')
      return
    }
    setUploading(true)
    try {
      const url = await uploadAvatarPhoto(userId, file)
      if (!url) throw new Error('Upload failed')
      await persistAvatar({ avatarType: 'photo', avatarUrl: url })
    } catch (err) {
      console.error('Failed to upload photo:', err)
      notify.error(err?.message || 'Failed to upload photo')
    } finally {
      setUploading(false)
      // reset input so same file can be re-selected after error
      event.target.value = ''
    }
  }

  const handleSaveGoogle = () => {
    if (!derivedGoogleUrl) {
      notify.error('No Google profile picture available. Link your Google account first.')
      return
    }
    persistAvatar({ avatarType: 'google', avatarUrl: derivedGoogleUrl })
  }

  const handleClearAvatar = () => persistAvatar({ avatarType: 'initials', avatarUrl: null })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Change avatar</DialogTitle>
          <DialogDescription>Pick an icon, upload a photo, or use your Google avatar.</DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList className="w-full">
            <TabsTrigger
              value="icon"
              className="flex-1 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:font-semibold data-[state=active]:shadow-md data-[state=inactive]:text-muted-foreground"
            >
              Icon
            </TabsTrigger>
            <TabsTrigger
              value="photo"
              className="flex-1 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:font-semibold data-[state=active]:shadow-md data-[state=inactive]:text-muted-foreground"
            >
              Photo
            </TabsTrigger>
            <TabsTrigger
              value="google"
              className="flex-1 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:font-semibold data-[state=active]:shadow-md data-[state=inactive]:text-muted-foreground"
              disabled={!derivedGoogleUrl}
            >
              Google
            </TabsTrigger>
          </TabsList>

          <TabsContent value="icon">
            <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 py-2" role="radiogroup" aria-label="Sports icons">
              {ICON_KEYS.map((iconKey) => {
                const Icon = AVATAR_ICON_LIBRARY[iconKey]
                const isSelected = selectedIcon === iconKey
                return (
                  <button
                    key={iconKey}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    aria-label={iconKey}
                    onClick={() => setSelectedIcon(iconKey)}
                    className={cn(
                      'h-14 w-14 rounded-full border-2 flex items-center justify-center transition-colors',
                      isSelected
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:bg-muted',
                    )}
                  >
                    <Icon className="h-6 w-6 text-primary" />
                    {isSelected && <Check className="absolute -mt-8 ml-8 h-4 w-4 text-primary" />}
                  </button>
                )
              })}
            </div>
            <div className="flex gap-2 justify-end mt-3">
              <Button variant="ghost" onClick={handleClearAvatar} disabled={saving}>Use initials</Button>
              <Button onClick={handleSaveIcon} disabled={saving || !selectedIcon}>Save icon</Button>
            </div>
          </TabsContent>

          <TabsContent value="photo">
            <div className="flex flex-col items-center gap-3 py-4">
              <Avatar
                size="xl"
                avatarType={currentAvatarType}
                avatarUrl={currentAvatarUrl}
              />
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-full border border-input bg-background hover:bg-muted text-sm font-medium transition-colors">
                <Upload className="h-4 w-4" />
                {uploading ? 'Uploading…' : 'Choose photo'}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePickFile}
                  disabled={uploading || saving}
                />
              </label>
              <p className="text-xs text-muted-foreground">PNG or JPG, up to ~5 MB recommended.</p>
            </div>
          </TabsContent>

          <TabsContent value="google">
            <div className="flex flex-col items-center gap-3 py-4">
              {derivedGoogleUrl ? (
                <>
                  <Avatar size="xl" avatarType="google" avatarUrl={derivedGoogleUrl} />
                  <Button onClick={handleSaveGoogle} disabled={saving}>Use Google photo</Button>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">Link your Google account in Settings to use it here.</p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
